import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/carousel/styles.css';
import 'mantine-datatable/styles.layer.css';
import { Authenticated } from '@refinedev/core';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import AuthenticatedRoute from './auth/AuthenticatedRoute';
import { RedirectToLoginPage } from './auth/RedirectToLoginPage';
import ClientsPage from './clients/ClientsPage';
import RedirectUnknownClient from './clients/RedirectUnknownClient';
import { useIsAfternoon } from './common/useIsAfternoonHook';
import { useFlags } from './feature-flags/FlagsHook';
import { useCurrentClientIfAvailable } from './clients/context/CurrentClientProvider';
import DetectedEntitiesPage from './data-capture/detected-entities/DetectedEntitiesPage';
import { DebugPage } from './debug/DebugPage';
import ViewEntityPage from './financial-data/ViewEntityPage';
import ClientData, { ClientTabs } from './home/ClientData';
import { CompleteConnectionFlowPage } from 'src/financial/connections/CompleteConnectionFlowPage';
import SecretMenuPage from './secret-menu/SecretMenuPage';
import { AppContainer, DeprecatedAppContainer } from './UI/app-container';
import { useClients } from 'src/clients/hooks/useClients';
import PageLoader from 'src/UI/loader/PageLoader';
import CreateFinancialItemPage from 'src/financial/financial-data/financial-item/CreateFinancialItemPage';
import ViewAndEditFinancialItemPage from 'src/financial/financial-data/financial-item/ViewAndEditFinancialItemPage';
import AdminPage from './admin/AdminPage';
import { useSessionData } from './auth/SessionDataHook';
import useCurrentUser from './admin/users/hooks/useCurrentUser';
import UserDetailsPage from './user/UserDetailsPage';
import { GenericErrorPage } from './error/GenericErrorPage';
import CompanyPage from './company/CompanyPage';
import FundDetailPage from 'src/company/funds/components/FundDetailPage';
import { OAuthRedirectPage } from 'src/meetings/oauth-redirect/OAuthRedirectPage';
import { MeetingsPage } from 'src/meetings/home/MeetingsPage';
import { MeetingDetailPage } from 'src/meetings/detail/MeetingDetailPage';
import SuitabilityReportEditPage from 'src/suitability-report/edit/SuitabilityReportEditPage';
import { LoginPage } from 'src/auth/LoginPage';

export default function AllRoutesNew() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<RedirectToLoginPage />} />
      <Route path="*" element={<ProtectedRoutesWithSessionNew />} />
    </Routes>
  );
}

function ProtectedRoutesWithSessionNew() {
  const { isLoading, session } = useSessionData();

  if (isLoading) return <PageLoader />;
  if (!session) return <RedirectToLoginPage />;

  return <ProtectedRoutesWithUserCheck />;
}

function AdminRoute() {
  const { currentUser, isLoading } = useCurrentUser();
  const location = useLocation();

  if (isLoading) return <PageLoader />;

  const isAdmin = currentUser?.userRole === 'SYSTEM_ADMIN';

  if (!isAdmin) {
    const fallback = location.state?.from?.pathname || '/';
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}

function ProtectedRoutesWithUserCheck() {
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();

  if (isLoadingUser) return <PageLoader />;

  if (!currentUser) {
    return <GenericErrorPage error="Current user is not available. Please try logging in again." />;
  }

  if (!['ACTIVE', 'PENDING'].includes(currentUser.userStatus)) {
    return (
      <GenericErrorPage
        error="Your account is not active. Please contact your system administrator."
        title="Account inactive"
        text="Your account is not active. Please contact your system administrator."
        showRefresh={false}
      />
    );
  }

  return <ProtectedRoutes />;
}

function ProtectedRoutes() {
  const { isAfternoon } = useIsAfternoon();
  const { isLoading } = useClients();
  const { flagEnabledDocumentEdit } = useFlags();

  if (isLoading) return <PageLoader />;

  return (
    <Routes>
      <Route path="/logout" element={<RedirectToLoginPage />} />
      <Route path="/" element={<AuthenticatedRoute />}>
        {/* Client-related routes */}
        <Route index element={<Navigate to={'/clients/all'} replace />} />
        <Route element={<DeprecatedAppContainer showChat />}>
          <Route path="clients/:tab/:id?" element={<ClientsPage />} />
        </Route>
        <Route path="client/undefined" element={<RedirectUnknownClient />} />

        {flagEnabledDocumentEdit && (
          <Route
            path="client/:clientId/document/suitability-reports/:id/edit"
            element={<AppContainer />}
          >
            <Route index element={<SuitabilityReportEditPage />} />
          </Route>
        )}

        <Route path="client/:clientId">
          <Route element={<DeprecatedAppContainer showChat />}>
            <Route index element={<ClientRootRedirect />} />
            <Route path=":tab/:sectionSlug/folder/:folderId" element={<ClientData />} />
            <Route path=":tab/:sectionSlug?" element={<ClientData />} />
          </Route>
          <Route element={<DeprecatedAppContainer bg="white" showChat={false} />}>
            <Route path="detected-entities" element={<DetectedEntitiesPage />} />
            <Route path="financial-items/create/:product" element={<CreateFinancialItemPage />} />
            <Route
              path="financial-items/:product/:id/"
              element={<ViewAndEditFinancialItemPage />}
            />
          </Route>
        </Route>

        <Route element={<DeprecatedAppContainer />}>
          <Route path="user/:tab/:id?" element={<UserDetailsPage />} />
        </Route>

        {/* Company-related routes */}
        <Route element={<DeprecatedAppContainer showChat />}>
          <Route path="company/:tab?/" element={<CompanyPage />} />
          <Route path="company/:tab?/:fundId?" element={<FundDetailPage />} />
        </Route>

        {/* Admin-related routes */}
        <Route path="admin" element={<AdminRoute />}>
          <Route
            path=":tab/:id?"
            element={
              <DeprecatedAppContainer showChat>
                <AdminPage />
              </DeprecatedAppContainer>
            }
          />
        </Route>
      </Route>
      {isAfternoon && (
        <Route
          path="/debug"
          element={
            <Authenticated
              key="protected"
              fallback={<RedirectToLoginPage />}
              v3LegacyAuthProviderCompatible={false}
            >
              <DebugPage />
            </Authenticated>
          }
        />
      )}
      <Route
        path="/complete-connection-flow"
        element={
          <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
            <CompleteConnectionFlowPage />
          </Authenticated>
        }
      />
      {isAfternoon && (
        <Route
          element={
            <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
              <DeprecatedAppContainer showChat={false} />
            </Authenticated>
          }
        >
          <Route path="secret-menu/:tab?/" element={<SecretMenuPage />} />
        </Route>
      )}
      <Route
        path="/view-entity"
        element={
          <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
            <DeprecatedAppContainer showChat={true}>
              <ViewEntityPage />
            </DeprecatedAppContainer>
          </Authenticated>
        }
      />
      <Route
        path="/oauth-callback/google-calendar"
        element={
          <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
            <DeprecatedAppContainer showChat={false}>
              <OAuthRedirectPage platform={'GOOGLE'} />
            </DeprecatedAppContainer>
          </Authenticated>
        }
      />
      <Route
        path="/oauth-callback/microsoft-calendar"
        element={
          <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
            <DeprecatedAppContainer showChat={false}>
              <OAuthRedirectPage platform={'MICROSOFT'} />
            </DeprecatedAppContainer>
          </Authenticated>
        }
      />
      <Route
        path="/meetings"
        element={
          <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
            <DeprecatedAppContainer showChat={false} p={0} bg="white" />
          </Authenticated>
        }
      >
        <Route index element={<MeetingsPage />} />
      </Route>
      <Route
        path="/meetings"
        element={
          <Authenticated key="protected" fallback={<RedirectToLoginPage />}>
            <DeprecatedAppContainer showChat={false} p={32} bg="white" />
          </Authenticated>
        }
      >
        <Route path=":eventId" element={<MeetingDetailPage />} />
        <Route path=":eventId/:tab" element={<MeetingDetailPage />} />
      </Route>
    </Routes>
  );
}

function ClientRootRedirect() {
  const { clientId } = useCurrentClientIfAvailable();
  const clientDataRootPath = `/client/${clientId}/${ClientTabs.NonFinancialData}`;

  return <Navigate to={clientDataRootPath} replace />;
}
